import React, { useState } from "react";
import { Flag, Loader, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { reports as reportsApi } from "../services/api";

const REASONS = ["spam", "inappropriate", "harassment", "private_info", "copyright", "other"];
// private_info maps to the closest backend reason; kept distinct in UI copy
const REASON_TO_API = { private_info: "inappropriate" };

// Modal to report a post or comment (Play UGC / Apple 1.2 compliance)
function ReportDialog({ contentType, contentId, onClose }) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!reason) return;
    setSubmitting(true);
    setError(null);
    try {
      await reportsApi.create(token, contentType, contentId, REASON_TO_API[reason] || reason, details);
      setDone(true);
    } catch (err) {
      setError(err.message || t("report.failed"));
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[2000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="bg-th-card border border-th-border rounded-2xl p-5 max-w-sm w-full">
        {done ? (
          <div className="text-center py-4">
            <Check size={28} className="mx-auto text-th-accent mb-3" />
            <p className="text-sm text-th-text">{t("report.thanks")}</p>
            <button
              onClick={onClose}
              className="mt-4 text-sm font-medium text-th-bg bg-th-accent px-6 py-2 rounded-xl"
            >
              {t("report.close")}
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-th-text flex items-center gap-1.5">
                <Flag size={14} className="text-th-accent" />
                {t("report.title")}
              </p>
              <button onClick={onClose} className="text-th-muted hover:text-th-text">
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-th-muted mb-3">{t("report.anonymous")}</p>
            <div className="space-y-1.5">
              {REASONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setReason(r)}
                  className={`w-full text-left text-xs px-3 py-2 rounded-lg border transition-colors ${
                    reason === r
                      ? "bg-th-accent-dim border-th-accent-border text-th-accent"
                      : "bg-th-surface border-th-border text-th-text-sec hover:border-th-text-sec"
                  }`}
                >
                  {t(`report.reason_${r}`)}
                </button>
              ))}
            </div>
            {reason === "other" && (
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                maxLength={500}
                rows={2}
                placeholder={t("report.detailsPlaceholder")}
                className="w-full mt-2 text-xs text-th-text bg-th-surface border border-th-border rounded-xl px-3 py-2 focus:outline-none focus:border-th-accent-border resize-none"
              />
            )}
            {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
            <button
              onClick={submit}
              disabled={!reason || submitting}
              className="w-full mt-3 flex items-center justify-center gap-1.5 text-sm font-medium text-th-bg bg-th-accent py-2.5 rounded-xl disabled:opacity-40"
            >
              {submitting ? <Loader size={14} className="animate-spin" /> : <Flag size={14} />}
              {t("report.submit")}
            </button>
            <p className="text-[11px] text-th-muted text-center mt-2.5">{t("report.disclaimer")}</p>
          </>
        )}
      </div>
    </div>
  );
}

export default ReportDialog;
